import { Router } from '@angular/router';
import { Injectable, ErrorHandler } from '@angular/core';
import * as stacktrace from 'stacktrace-js';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Platform } from '@ionic/angular';
import { LoaderService } from './loader.service';
import { ConfigService } from './config.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environ } from '../app.config';

declare var FirebasePlugin;

@Injectable({ providedIn: 'root' })
export class CrashService implements CrashService {
  public Tag = 'CrashService';
  deviceReady = false;
  debug = true;
  environment = environ;

  constructor(
    private firebase: FirebaseX,
    private plt: Platform,
    private ionLoader: LoaderService,
    private router: Router
  ) {
    if (this.environment === 'prod') {
      this.debug = false;
    }
    this.log('Initializing global untrapped error handler');
    // this.trapOnDesktop();
  }

  trapOnDesktop() {
    this.log('Initializing desktop untrapped error handler');
    const ionLoader = this.ionLoader;
    // Send an unhandled JS exception
    const appRootURL = window.location.href.replace('index.html', '');
    window.onerror = (errorMsg, url, line, col, error) => {
      this.log('JS exception on desktop - ', errorMsg);
      this.setCustomKey('error_data', {
        error_type: 'js-exception',
        source: 'window-on-error',
        error_message: error.message || 'unknown',
        error_name: error.name || 'unknown',
        error_time: new Date().toISOString(),
      });
      let stackTrace = null;
      let logMessage = errorMsg;

      const sendError = () => {
        switch (errorMsg) {
          case `Uncaught TypeError: Cannot read property 'messageHandlers' of undefined`:
            this.log('Ignore cordova errors, running in browser');
            break;
          default:
            this.log(`Unhandled JS exception: ${logMessage}`, error);
        }
        ionLoader.hideLoader('unhandled JS exception');
      };

      logMessage +=
        ': url=' +
        url.replace(appRootURL, '') +
        '; line=' +
        line +
        '; col=' +
        col;

      if (typeof error === 'object') {
        stacktrace.fromError(error).then((trace) => {
          stackTrace = trace;
          sendError();
        });
      } else {
        sendError();
      }
    };
  }

  trapOnDevice() {
    this.log('Initializing device untrapped error handler');
    this.deviceReady = true;
    // Send an unhandled JS exception
    const appRootURL = window.location.href.replace('index.html', '');
    window.onerror = function (errorMsg, url, line, col, error) {
      this.log('JS exception on device - ', errorMsg);
      let stackTrace = null;
      let logMessage = errorMsg;

      const sendError = () => {
        this.log('JS exception on device - ', errorMsg);
        this.setCustomKey('error_data', {
          error_type: 'js-exception',
          source: 'window-on-error',
          error_message: error.message || 'unknown',
          error_name: error.name || 'unknown',
          error_time: new Date().toISOString(),
        });
        if (!this.isMobileApp()) {
          this.log(`Unhandled error: ${logMessage}`, error);
          return;
        }
        this.firebase.logError(
          logMessage,
          stackTrace,
          function () {
            this.log('Sent js exception crashlytics event');
          },
          function (err) {
            this.log('Failed to send js exception crashlytics event', err);
          }
        );
      };

      logMessage +=
        ': url=' +
        url.replace(appRootURL, '') +
        '; line=' +
        line +
        '; col=' +
        col;

      if (typeof error === 'object') {
        stacktrace.fromError(error).then((trace) => {
          stackTrace = trace;
          sendError();
        });
      } else {
        sendError();
      }
    };
  }

  mockCrash() {
    this.log('Mocked another crash');
    this.firebase.logMessage('mocked another crash');
    this.firebase.sendCrash();
  }

  mockNonFatal() {
    this.log('Mock a non-fatal error');
    FirebasePlugin.setCrashlyticsCustomKey(
      'some-key',
      'Ipsum lorem data more',
      (response) => {
        this.log('setCrashlyticsCustomKey succeeded', response);
      },
      (response) => {
        this.log('setCrashlyticsCustomKey failed', response);
      }
    );
    this.firebase
      .logError('Keyed non-fatal error test', () => {
        this.log('mocked a keyed non-fatal error');
      })
      .then((response) => {
        this.log('Keyed non-fatal error response', response);
      });
  }

  setCustomKey(key, errorData) {
    const value = JSON.stringify(errorData);
    this.log(`crashyltics set error_data`, value);
    if (!this.isMobileApp()) {
      return;
    }
    FirebasePlugin.setCrashlyticsCustomKey(
      key,
      value,
      (response) => {
        this.log('setCrashlyticsCustomKey succeeded', response);
      },
      (response) => {
        this.log('setCrashlyticsCustomKey failed', response);
      }
    );
  }

  isMobileApp() {
    if (!this.deviceReady) {
      return false;
    }
    if (!this.plt.is('cordova')) {
      return false;
    }
    if (this.plt.is('desktop')) {
      return false;
    }
    if (this.plt.is('ios')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }
    if (this.plt.is('android')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  subscription(source, subscriptionType, error) {
    this.log('subscription error', error);
    // stacktrace.fromError(error).then((trace) => {
    this.setCustomKey('error_data', {
      error_type: 'subscription-error',
      source: source || 'unknown',
      subscription_type: subscriptionType || 'unknown',
      error_code: error.code || 'unknown',
      error_message: error.message || 'unknown',
      error_name: error.name || 'unknown',
      error_time: new Date().toISOString(),
    });
    if (this.isMobileApp()) {
      this.firebase.logError('subscription_error').then(
        () => {
          this.log('Sent subscription error event');
        },
        (err) => {
          this.log('Failed to send subscription error event', err);
        }
      );
    } else {
      this.log('running in  browser, do not send crashlytics data');
    }
    // });
  }

  process(source: string, sourceFunc: string, errorMessage: string, data: any) {
    const errorData = {
      error_type: 'process-error',
      source,
      source_func: sourceFunc,
      error_message: errorMessage,
      data,
      error_time: new Date().toISOString(),
    };
    this.log(`processing error - ${errorMessage}`, errorData);
    this.setCustomKey('error_data', errorData);
    if (this.isMobileApp()) {
      this.firebase.logError('process_error').then(
        () => {
          this.log('Sent process error event', errorData);
        },
        (error) => {
          this.log('Failed to send process error event', error);
        }
      );
    } else {
      this.log('running in  browser, do not send crashlytics data');
    }
  }

  iap(source: string, sourceFunc: string, errorMessage: string, data: any) {
    const errorData = {
      error_type: 'iap-error',
      source,
      source_func: sourceFunc,
      error_message: errorMessage,
      data,
      error_time: new Date().toISOString(),
    };
    this.log(`iap error - ${errorMessage}`, errorData);
    this.setCustomKey('error_data', errorData);
    if (this.isMobileApp()) {
      this.firebase.logError('iap_error').then(
        () => {
          this.log('Sent iap error event', errorData);
        },
        (error) => {
          this.log('Failed to send iap error event', error);
        }
      );
    } else {
      this.log('running in browser, do not send crashlytics data');
    }
  }

  query(source, errorMessage) {
    const errorData = {
      error_type: 'query-error',
      source,
      error_message: errorMessage,
      error_time: new Date().toISOString(),
    };
    this.log('query error', errorData);
    this.setCustomKey('error_data', errorData);
    if (this.isMobileApp()) {
      this.firebase.logError('query_error').then(
        () => {
          this.log('Sent query error event', errorData);
        },
        (error) => {
          this.log('Failed to send query error event', error);
        }
      );
    } else {
      this.log('running in  browser, do not send crashlytics data');
    }
  }

  setUserId(analyticsId: string) {
    this.log(`Crashyltics set 'uid' to '${analyticsId}'`);
    if (!this.isMobileApp()) {
      return;
    }
    this.log(`Running on device, save Crashlytics data`);
    this.firebase.setCrashlyticsUserId(analyticsId);
  }

  log(msg, data?) {
    if (this.debug) {
      if (data) {
        console.warn(msg, data);
      } else {
        console.warn(msg);
      }
    }
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.log('handleError() - ErrorEvent', error);
    } else {
      console.log('handleError() - other error', error);
    }
    if (
      error?.message?.includes(
        `Cannot read property 'currentTeamId' of undefined`
      )
    ) {
      this.router.navigate(['/splash']);
    }
  }
}
